import axios from 'axios';
import actions from './actions';
import { appOperations } from '../../../../../App/store';
import ColoredSnackbarConstants from '../../../../misc/ColoredSnackbar/ColoredSnackbarConstants';

const { setUser, setExports, setLoading, setCredits, setSettings } = actions;
const { openSnack } = appOperations;

const fetchUserAndExports = (userId, page = 1, pageSize = 12) => async (dispatch, getState) => {
    const { user } = getState().userInfo;
    if (!user) {
        dispatch(setLoading(true));
    }

    try {
        const userResponse = await axios.get(`/api/admin/Users/GetUser?userId=${userId}`);
        const { user: fetchedUser } = userResponse.data;
        dispatch(setUser(fetchedUser));

        const exportsResponse = await axios.get(`/api/admin/Users/GetUserExports?userId=${userId}&page=${page}&pageSize=${pageSize}`);
        const { exports: fetchedExports = [] } = exportsResponse.data;

        dispatch(setExports(fetchedExports));

    } catch (error) {
        dispatch(openSnack('Failed to fetch user data.', 'error'));
    } finally {
        const { loading } = getState().userInfo;
        if (loading) {
            dispatch(setLoading(false));
        }
    }
};

const updateUserCredits = (userId, credits) => async (dispatch) => {
    try {
        await axios.post('/api/admin/Users/UpdateUserCredits', { 
            userId, 
            ...credits 
        });
        
        dispatch(setCredits(credits));
        dispatch(fetchUserAndExports(userId));
        dispatch(openSnack('Credits updated successfully.', 'success'));
    } catch (error) {
        dispatch(openSnack('Failed to update credits.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    }
};

const updateUserSettings = (userId, settings) => async (dispatch) => {
    try {
        await axios.post('/api/admin/Users/UpdateUserSettings', { 
            userId, 
            ...settings 
        });

        dispatch(setSettings(settings));
        dispatch(fetchUserAndExports(userId));
        dispatch(openSnack('Settings updated successfully.', 'success'));
    } catch (error) {
        dispatch(openSnack('Failed to update settings.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    }
};

const deleteUser = (userId) => async (dispatch) => {
    try {
        await axios.post(`/api/admin/Users/DeleteUser?userId=${userId}`);
        dispatch(openSnack('User deleted successfully.', 'success'));
    } catch (error) {
        dispatch(openSnack('Failed to delete user.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    }
};

const deleteVideoExport = (userId, exportId, page, pageSize) => async (dispatch) => {
    try {
        await axios.post(`/api/admin/Export/DeleteVideoExportForUser/${exportId}?userId=${userId}`);
        dispatch(openSnack('Export deleted successfully.', 'success'));
        dispatch(fetchUserAndExports(userId, page, pageSize));
    } catch (error) {
        dispatch(openSnack('Failed to delete export.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    }
};

const retryExport = (userId, exportId, page, pageSize) => async (dispatch) => {
    try {
        await axios.post(`/api/admin/Export/RetryExport?exportId=${exportId}&userId=${userId}`);
        dispatch(openSnack('Export retry initiated.', 'success'));
        dispatch(fetchUserAndExports(userId, page, pageSize));
    } catch (error) {
        dispatch(openSnack('Failed to retry export.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    }
};

const fetchExportPreviewUrl = (fileName) => async (dispatch) => {
    try {
        const response = await axios.get('/api/admin/Export/GetUserExportPlayerUrl', {
            params: { fileName }
        });
        
        if (response.data && response.data.url) {
            return response.data.url;
        }
        dispatch(openSnack('Failed to fetch preview URL.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    } catch (error) {
        dispatch(openSnack('Failed to fetch preview URL.', ColoredSnackbarConstants.SNACKBAR_ERROR));
    }
};

const transferVideoSettings = (sourceExportId, targetUserId, targetVideoId, copyMediaFiles = true) => async (dispatch) => {
    try {
        await axios.post('/api/admin/Export/TransferVideoSettings', {
            sourceExportId,
            targetUserId,
            targetVideoId,
            copyMediaFiles,
        });
        dispatch(openSnack('Settings successfully applied.', 'success'));
        return true;
    } catch (error) {
        const message = error?.response?.data?.message || error?.response?.data?.Message || 'Failed to transfer settings.';
        dispatch(openSnack(message, ColoredSnackbarConstants.SNACKBAR_ERROR));
        return false;
    }
};

const updateUserEmail = (userId, newEmail) => async (dispatch) => {
    try {
        await axios.post('/api/admin/Users/UpdateUserEmail', {
            userId,
            newEmail
        });
        dispatch(fetchUserAndExports(userId));
        dispatch(openSnack('Email updated successfully.', 'success'));
    } catch (error) {
        const message = error?.response?.data?.message || error?.response?.data?.Message || 'Failed to update email.';
        dispatch(openSnack(message, ColoredSnackbarConstants.SNACKBAR_ERROR));
    }
};

export default {
    fetchUserAndExports,
    updateUserCredits,
    updateUserSettings,
    deleteUser,
    deleteVideoExport,
    retryExport,
    fetchExportPreviewUrl,
    transferVideoSettings,
    updateUserEmail,
};
